import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Alert } from '@cbhq/cds-web/overlays';

type UnsavedChangesAlertProps = {
  visible: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
} & ({ variant: 'edit'; memberName: string } | { variant: 'invite'; memberName?: never });

const messages = defineMessages({
  editTitle: {
    id: 'Team.unsavedChangesAlert.title',
    defaultMessage: 'You have unsaved changes',
    description: 'Unsaved changes alert title in edit member tray',
  },
  editBody: {
    id: 'Team.unsavedChangesAlert.body',
    defaultMessage: "Your edits to {memberName}'s access will be lost if you leave.",
    description: 'Unsaved changes alert body in edit member tray',
  },
  editKeepEditing: {
    id: 'Team.unsavedChangesAlert.keepEditing',
    defaultMessage: 'Keep editing',
    description: 'Keep editing action label in unsaved changes alert',
  },
  editDiscardChanges: {
    id: 'Team.unsavedChangesAlert.discardChanges',
    defaultMessage: 'Discard changes',
    description: 'Discard changes action label in unsaved changes alert',
  },
  inviteTitle: {
    id: 'Team.unsavedChangesAlert.invite.title',
    defaultMessage: 'You have an unsent invite',
    description: 'Unsaved changes alert title in invite team tray',
  },
  inviteBody: {
    id: 'Team.unsavedChangesAlert.invite.body',
    defaultMessage: 'Your invite details will be lost if you leave.',
    description: 'Unsaved changes alert body in invite team tray',
  },
  inviteKeepEditing: {
    id: 'Team.unsavedChangesAlert.invite.keepEditing',
    defaultMessage: 'Keep editing',
    description: 'Keep editing action label in unsaved invite alert',
  },
  inviteDiscardChanges: {
    id: 'Team.unsavedChangesAlert.invite.discardChanges',
    defaultMessage: 'Discard invite',
    description: 'Discard invite action label in unsaved invite alert',
  },
});

export function UnsavedChangesAlert(props: UnsavedChangesAlertProps) {
  const { visible, onKeepEditing, onDiscard } = props;
  const { formatMessage } = useIntl();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onDiscard();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onDiscard]);

  const content =
    props.variant === 'invite'
      ? {
          title: formatMessage(messages.inviteTitle),
          body: formatMessage(messages.inviteBody),
          keepEditingLabel: formatMessage(messages.inviteKeepEditing),
          discardLabel: formatMessage(messages.inviteDiscardChanges),
          testID: 'team-invite-unsaved-changes-alert',
        }
      : {
          title: formatMessage(messages.editTitle),
          body: formatMessage(messages.editBody, { memberName: props.memberName }),
          keepEditingLabel: formatMessage(messages.editKeepEditing),
          discardLabel: formatMessage(messages.editDiscardChanges),
          testID: 'team-member-unsaved-changes-alert',
        };

  return (
    <Alert
      title={content.title}
      body={content.body}
      visible={visible}
      stacked
      onRequestClose={onKeepEditing}
      preferredActionLabel={content.discardLabel}
      onPreferredActionPress={onDiscard}
      dismissActionLabel={content.keepEditingLabel}
      onDismissActionPress={onKeepEditing}
      actionLayout="vertical"
      testID={content.testID}
    />
  );
}
